import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API, api } from "@/lib/api";
import { fmtDate, fmtEuro, fmtNum } from "@/lib/format";
import { ArrowLeft, CalendarCheck, CheckCircle2, Download, Loader2, MapPin, PenLine, ShieldCheck } from "lucide-react";

function RecapItem({ label, value, testid }) {
  return (
    <div className="border border-stone-800 p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
      <p data-testid={testid} className="mt-1 text-sm text-stone-200">{value}</p>
    </div>
  );
}

export default function ReportPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/estimations/${id}`)
      .then((r) => setDoc(r.data))
      .catch(() => setError("Ce rapport est introuvable ou a expiré."));
  }, [id]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0B0B0D] px-6 text-center text-[#F5F3EF]">
        <p data-testid="report-error" className="font-display text-2xl text-amber-100">{error}</p>
        <Link to="/estimation" data-testid="report-error-cta" className="btn-gold mt-8">
          Refaire une estimation
        </Link>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0B0D]">
        <Loader2 data-testid="report-loading" className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }

  const { lead, property, result } = doc;
  const typeLabel = property.type_bien === "maison" ? "Maison" : "Appartement";
  const etageLabel =
    property.type_bien === "appartement"
      ? property.etage === 0
        ? "Rez-de-chaussée"
        : `${property.etage}${property.etage === 1 ? "er" : "e"} étage${property.ascenseur ? " avec ascenseur" : property.etage >= 2 ? " sans ascenseur" : ""}`
      : "—";

  return (
    <div data-testid="report-page" className="print-light min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      <header className="no-print border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="report-brand-link" className="font-display text-xl text-[#F5F3EF]">
            Valeur <span className="italic text-[#D4AF37]">Prestige</span>
          </Link>
          <button
            type="button"
            data-testid="report-download-button"
            onClick={() => window.open(`${API}/estimations/${id}/pdf`, "_blank")}
            className="btn-gold !px-5 !py-2.5 !text-xs"
          >
            <Download size={14} /> Télécharger le rapport (PDF)
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">
          Rapport d'estimation confidentiel — édité le {fmtDate(doc.created_at)}
        </p>
        <h1 data-testid="report-title" className="font-display mt-4 text-3xl font-light text-amber-100 sm:text-4xl">
          Estimation de votre bien, <em className="print-gold text-[#D4AF37]">{lead.prenom} {lead.nom}.</em>
        </h1>
        <p data-testid="report-address" className="mt-4 flex items-center gap-2 text-sm text-stone-400">
          <MapPin size={14} className="text-[#D4AF37]" />
          {typeLabel} — {property.rue}, {property.code_postal} {property.ville} · {result.zone_label}
        </p>
        {doc.email_prospect && (
          <p data-testid="report-email-note" className="mt-3 text-xs text-stone-500">
            Un exemplaire de ce rapport vient d'être envoyé à {lead.email}.
          </p>
        )}

        <div className="mt-12 border border-[#D4AF37]/25 bg-[#141417] p-8 sm:p-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">Fourchette de valeur vénale</p>
          <div className="mt-6 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p data-testid="report-price-range" className="font-display text-3xl font-light text-[#E5C158] print-gold sm:text-5xl">
                {fmtEuro(result.bas)} — {fmtEuro(result.haut)}
              </p>
              <p data-testid="report-price-median" className="mt-3 text-sm text-stone-400">
                Valeur médiane estimée : <span className="text-stone-200">{fmtEuro(result.median)}</span>
              </p>
              {result.donnees === "dvf" ? (
                <p data-testid="report-data-source" className="mt-4 inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                  <ShieldCheck size={12} /> Comparables : transactions réelles DVF autour de votre adresse
                </p>
              ) : (
                <p data-testid="report-data-source" className="mt-4 inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                  Données de marché sectorielles — estimation indicative
                </p>
              )}
            </div>
            <div className="border border-stone-700 px-5 py-4 text-right">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Prix au m²</p>
              <p data-testid="report-price-m2" className="font-display mt-1 text-2xl text-[#E5C158] print-gold">
                {fmtNum(result.prix_m2)} €/m²
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <RecapItem label="Type" value={typeLabel} testid="recap-type" />
          <RecapItem label="Surface totale" value={`${fmtNum(property.surface)} m²`} testid="recap-surface" />
          {property.surface_carrez && (
            <RecapItem label="Surface loi Carrez" value={`${fmtNum(property.surface_carrez)} m²`} testid="recap-carrez" />
          )}
          {property.exterieur && property.exterieur !== "aucun" && (
            <RecapItem
              label="Extérieur"
              value={property.exterieur.charAt(0).toUpperCase() + property.exterieur.slice(1)}
              testid="recap-exterieur"
            />
          )}
          <RecapItem label="Pièces" value={`${property.pieces} pièce${property.pieces > 1 ? "s" : ""}${property.chambres ? ` · ${property.chambres} ch.` : ""}`} testid="recap-pieces" />
          <RecapItem label="Étage" value={etageLabel} testid="recap-etage" />
          <RecapItem label="DPE" value={`Classe ${property.dpe}`} testid="recap-dpe" />
          <RecapItem label="État" value={property.etat} testid="recap-etat" />
          <RecapItem label="Projet" value={property.delai_projet} testid="recap-projet" />
          <RecapItem label="Secteur" value={result.zone_label} testid="recap-zone" />
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl font-normal text-amber-100">
            Ventes <em className="print-gold text-[#D4AF37]">comparables</em> récentes
          </h2>
          <div className="mt-6 overflow-x-auto border border-stone-800">
            <table data-testid="report-comparables-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-stone-800 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  <th className="px-5 py-4">Adresse</th>
                  <th className="px-5 py-4">Surface</th>
                  <th className="px-5 py-4">Prix</th>
                  <th className="px-5 py-4">€/m²</th>
                  <th className="px-5 py-4">Vente</th>
                </tr>
              </thead>
              <tbody>
                {result.comparables.map((c, i) => (
                  <tr key={i} data-testid={`comparable-row-${i}`} className="border-b border-stone-800/60 text-stone-300 last:border-0">
                    <td className="px-5 py-4">{c.adresse}</td>
                    <td className="px-5 py-4">{fmtNum(c.surface)} m²</td>
                    <td className="px-5 py-4">{fmtEuro(c.prix)}</td>
                    <td className="px-5 py-4">{fmtNum(c.prix_m2)} €</td>
                    <td className="px-5 py-4">{c.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="border border-stone-800 p-8">
            <h3 className="font-display text-xl text-amber-200/90">Sources & références</h3>
            <ul data-testid="report-sources-list" className="mt-4 space-y-3 text-sm text-stone-400">
              {result.sources.map((s) => (
                <li key={s} className="flex gap-3">
                  <span className="text-[#D4AF37]">✦</span> {s}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs leading-relaxed text-stone-500">
              Estimation indicative fondée sur les données de marché disponibles à la date d'édition
              ({fmtDate(doc.created_at)}). Elle ne constitue pas un avis de valeur professionnel :
              seule une visite du bien permet une valorisation définitive.
            </p>
          </div>
          <div className="flex flex-col justify-between border border-[#D4AF37]/25 bg-[#141417] p-8">
            <div>
              <h3 className="font-display text-xl text-amber-200/90">La suite, avec votre conseiller dédié</h3>
              <p className="mt-4 text-sm leading-relaxed text-stone-400">
                Votre estimation est relue par un conseiller en immobilier de prestige qui connaît
                votre secteur. Avec votre accord, il vous recontacte personnellement pour l'affiner
                lors d'une visite confidentielle et sans engagement.
              </p>
              <p className="mt-4 flex items-center gap-2 text-xs text-stone-500">
                <ShieldCheck size={14} className="text-[#D4AF37]" />
                Vos données restent strictement confidentielles.
              </p>
            </div>
            <div className="no-print mt-8 flex flex-wrap gap-3">
              <button type="button" data-testid="report-pdf-button" onClick={() => window.open(`${API}/estimations/${id}/pdf`, "_blank")} className="btn-gold !text-xs">
                <Download size={14} /> Télécharger le PDF
              </button>
              <button type="button" data-testid="report-print-button" onClick={() => window.print()} className="btn-ghost !text-xs">
                Imprimer
              </button>
              <Link to="/" data-testid="report-home-link" className="btn-ghost !text-xs">
                <ArrowLeft size={14} /> Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>

        {doc.mandat ? (
          <div data-testid="mandat-signed-banner" className="mt-12 border border-emerald-500/30 bg-emerald-500/5 p-8 text-center sm:p-10">
            <CheckCircle2 size={24} className="mx-auto text-emerald-400" />
            <p className="font-display mt-3 text-xl text-emerald-300 sm:text-2xl">Mandat de vente signé</p>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-stone-400">
              Signé électroniquement par {doc.mandat.signataire} le {fmtDate(doc.mandat.signed_at)} —
              prix de mise en vente {fmtEuro(doc.mandat.prix_mise_en_vente)}. Barsamia vous recontacte sous 24 h ouvrées.
            </p>
          </div>
        ) : (
          <div data-testid="mandat-cta-banner" className="relative mt-12 overflow-hidden border border-[#D4AF37]/30 bg-[#141417] p-8 text-center sm:p-12">
            <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <p className="relative font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Passer à la vente</p>
            <h2 className="font-display relative mx-auto mt-4 max-w-xl text-2xl font-normal text-amber-100 sm:text-3xl">
              Cette estimation vous convainc ? <em className="text-[#D4AF37]">Signez votre mandat en ligne.</em>
            </h2>
            <p className="relative mx-auto mt-4 max-w-lg text-sm leading-relaxed text-stone-400">
              Prix de mise en vente conseillé {fmtEuro(result.median)} · honoraires 5 % TTC charge vendeur ·
              durée 3 mois · signature électronique simple, exemplaire envoyé par email.
            </p>
            <Link to={`/mandat/${id}`} data-testid="mandat-cta-button" className="btn-gold relative mt-8">
              <PenLine size={16} /> Signer mon mandat
            </Link>
          </div>
        )}

        {doc.rdv ? (
          <div data-testid="rdv-booked-banner" className="mt-6 border border-[#D4AF37]/30 bg-[#141417] p-8 text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Visite planifiée</p>
            <p className="font-display mt-3 text-xl text-amber-100">
              Rendez-vous le {fmtDate(doc.rdv.date)} à {doc.rdv.heure}
            </p>
          </div>
        ) : (
          <div data-testid="rdv-cta-banner" className="mt-6 flex flex-col items-center justify-between gap-6 border border-stone-800 bg-[#141417] p-8 text-center sm:flex-row sm:text-left">
            <div>
              <h2 className="font-display text-xl text-amber-100">
                Affiner cette estimation <em className="text-[#D4AF37]">en visite ?</em>
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-stone-400">
                Réservez directement un créneau d'une heure avec votre conseiller dédié — confirmation immédiate
                par email, invitation calendrier incluse.
              </p>
            </div>
            <Link to={`/rdv/${id}`} data-testid="rdv-cta-button" className="btn-ghost shrink-0">
              <CalendarCheck size={16} /> Prendre rendez-vous
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
